import { BaseModel, type RelationConfig } from './base';

import { Customer } from './customer';
import { Form } from './form';
import { FormItem } from './formItem';

// Enums

export enum CustomerFormRequestState {
  none = 0,
  requested = 1,
  completed = 2,
  updateRequested = 3,
  scheduled = 4,
}

export const CustomerFormRequestStateLabels: Record<CustomerFormRequestState, string> = {
  [CustomerFormRequestState.none]: 'general.none',
  [CustomerFormRequestState.requested]: 'form.requested',
  [CustomerFormRequestState.completed]: 'form.completed',
  [CustomerFormRequestState.updateRequested]: 'form.updateRequested',
  [CustomerFormRequestState.scheduled]: 'general.scheduled',
};

export enum CustomerFormState {
  open = 0,
  locked = 1,
  reopened = 2,
}

export const CustomerFormStateLabels: Record<CustomerFormState, string> = {
  [CustomerFormState.open]: 'general.open',
  [CustomerFormState.locked]: 'general.locked',
  [CustomerFormState.reopened]: 'form.reopened',
};

// Main Model

export class CustomerForm extends BaseModel {
  idCustomerForm!: number;
  consentAccepted!: boolean;
  sendEmail!: boolean;
  requestState!: CustomerFormRequestState;
  state!: CustomerFormState;
  customer!: Customer | null;
  form!: Form | null;
  formItems!: FormItem[];

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      form: { type: Form },
      formItems: { type: FormItem },
    };
  }

  override objectID(): string | null {
    return this.idCustomerForm?.toString() ?? null;
  }

  requestStateVariant(): string {
    switch (this.requestState) {
      case CustomerFormRequestState.requested:
        return 'warning';
      case CustomerFormRequestState.completed:
        return 'success';
      case CustomerFormRequestState.updateRequested:
        return 'warning';
      case CustomerFormRequestState.scheduled:
        return 'warning';
      default:
        return 'default';
    }
  }

  // Request state getters
  get isRequestStateNone(): boolean { return this.requestState === CustomerFormRequestState.none; }
  get isRequestStateRequested(): boolean { return this.requestState === CustomerFormRequestState.requested; }
  get isRequestStateCompleted(): boolean { return this.requestState === CustomerFormRequestState.completed; }
  get isRequestStateUpdateRequested(): boolean { return this.requestState === CustomerFormRequestState.updateRequested; }
  get isRequestStateScheduled(): boolean { return this.requestState === CustomerFormRequestState.scheduled; }

  // Form state getters
  get isFormStateOpen(): boolean { return this.state === CustomerFormState.open; }
  get isFormStateLocked(): boolean { return this.state === CustomerFormState.locked; }
  get isFormStateReopened(): boolean { return this.state === CustomerFormState.reopened; }

  get showContents(): boolean {
    if (this.isRequestStateRequested || this.isRequestStateScheduled) {
      return false;
    }
    return true;
  }

  get canRequest(): boolean {
    return this.state === CustomerFormState.open || this.state === CustomerFormState.reopened;
  }

  get isRequested(): boolean {
    return this.isRequestStateRequested || this.isRequestStateUpdateRequested;
  }
}
